import * as React from "react";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { SxProps } from "@mui/material";
import { DateTimeValidationError, renderTimeViewClock } from "@mui/x-date-pickers";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
interface IDatePicker {
  handleOnChange: (are: any) => void;
  value: Dayjs;
  label: string;
  maxDate?: Dayjs;
  minDate?: Dayjs;
  sx?: SxProps;
  minDateTime?: Dayjs;
  maxDateTime?: Dayjs;
  onError?: ((error: DateTimeValidationError, value: dayjs.Dayjs | null) => void) | undefined;
}
const today = new Date();

const CustomDateTimePicker: React.FC<IDatePicker> = ({ sx, handleOnChange, value, label, maxDate, minDate, minDateTime, maxDateTime, onError }) => {
  const dateTimeSxProps: SxProps = {
    /* ── Match SearchBox appearance ── */
    "& .MuiInputBase-root": {
      height: 32,
      minWidth: 155,
      maxWidth: 200,
      borderRadius: "6px",
      backgroundColor: "var(--card)",
      border: "1px solid var(--input)",
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
      fontSize: 14,
      color: "var(--foreground)",
      paddingRight: "8px",
      "&:hover": {
        borderColor: "var(--muted-foreground)",
      },
      "&.Mui-focused": {
        borderColor: "var(--ring)",
        boxShadow: "0 0 0 1px var(--ring)",
      },
      "& fieldset": {
        border: "none",
      },
    },
    "& .MuiInputBase-input": {
      padding: "0 12px",
      fontSize: 14,
      color: "var(--foreground)",
      height: "100%",
    },
    "& .MuiInputLabel-root": {
      display: "none",
    },
  };
  const pickerStyle = sx || dateTimeSxProps;

  const slotProps: SxProps = {
    openPickerButton: {
      sx: {
        color: "var(--muted-foreground)",
        width: 26,
        height: 26,
        paddingRight: 4,
        borderRadius: "4px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        "& .MuiSvgIcon-root": {
          fontSize: "1.3rem",
        },
        "&:hover": { 
          backgroundColor: "var(--accent)",
         },
      },
    },
    popper: {
      sx: {
        zIndex: 1400,
        "& .MuiPaper-root.MuiPickersPopper-paper": {
          backgroundColor: "var(--card)",
          color: "var(--card-foreground)",
          border: "1px solid var(--border)",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        },
        "& .MuiPickersDay-root": {
          color: "var(--foreground)",
          "&:hover": { backgroundColor: "var(--accent)" },
          "&.Mui-selected": {
            backgroundColor: "var(--primary)",
            color: "var(--primary-foreground)",
            "&:hover": { backgroundColor: "var(--primary)" },
          },
        },
        "& .MuiPickersCalendarHeader-label": {
          color: "var(--foreground)",
        },
        "& .MuiIconButton-root": {
          color: "var(--muted-foreground)",
        },
        "& .MuiClock-pin, & .MuiClockPointer-root": {
          backgroundColor: "var(--primary)",
        },
        "& .MuiClockPointer-thumb": {
          backgroundColor: "var(--primary)",
          borderColor: "var(--primary)",
        },
        "& .MuiClockNumber-root": {
          color: "var(--foreground)",
        },
        "& .MuiClock-clock": {
          backgroundColor: "var(--accent)",
        },
        "& .MuiDialogActions-root .MuiButton-root": {
          color: "var(--primary)",
        },
        "& .MuiDayCalendar-weekDayLabel": {
          color: "var(--muted-foreground)",
        },
        "& .MuiPickersYear-yearButton, & .MuiPickersMonth-monthButton": {
          color: "var(--foreground)",
          "&.Mui-selected": {
            backgroundColor: "var(--primary)",
            color: "var(--primary-foreground)",
          },
        },
        "& .MuiMultiSectionDigitalClockSection-item": {
          color: "var(--foreground)",
          "&.Mui-selected": {
            backgroundColor: "var(--primary)",
            color: "var(--primary-foreground)",
          },
        },
      },
    },
    textField: {
      placeholder: label,
    },
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker
          // renderInput={(params) => <TextField {...params} />}
          maxDate={maxDate && dayjs(maxDate)}
          minDate={minDate && dayjs(minDate)}
          minDateTime={minDateTime && dayjs(minDateTime)}
          maxDateTime={maxDateTime && dayjs(maxDateTime)}
          orientation="landscape"
          sx={pickerStyle}
          label={label}
          value={dayjs.utc(value || today).local()}
          onChange={(newValue) => handleOnChange(newValue)}
          viewRenderers={{
            hours: renderTimeViewClock,
            minutes: renderTimeViewClock,
            seconds: renderTimeViewClock,
          }}
          slotProps={slotProps}
          onError={onError}
        />
      </LocalizationProvider>
    </>
  );
};

export default CustomDateTimePicker;
